<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRouter } from "vue-router";

import { useSearchRoute } from "@/common/composables/use-search-route";
import { useSearchStore } from "@/stores";
import type { Synonym } from "@/types/article-formats/synonym";

const props = defineProps<{
  preferredSource: Synonym["source"];
  synonyms: Synonym[];
}>();

const { t } = useI18n();
const { getSearchRoute } = useSearchRoute(useRouter());
const { searchDirection } = storeToRefs(useSearchStore());

const searchRoute = (word: string) => {
  const route = getSearchRoute(word, searchDirection.value);

  // Reset to default tab after searching
  if (route.query) {
    delete route.query.tab;
  }

  return route;
};

const synonyms = computed<Synonym[]>(() =>
  props.synonyms.filter((synonym, _, synonyms) => {
    // We always want synonyms from the preferred source, and there shouldn't be duplicates from one source
    if (synonym.source === props.preferredSource) {
      return true;
    }

    // De-duplicate synonyms from other sources
    return !synonyms.find(
      (s) => s.synonym === synonym.synonym && s.source === props.preferredSource
    );
  })
);
</script>

<template>
  <div class="article-content-synonyms">
    <div class="article-content-synonyms__header">
      {{ t("article.synonyms") }}
    </div>
    <div class="article-content-synonyms__synonyms">
      <template v-for="(synonym, index) in synonyms" :key="index">
        <RouterLink
          v-if="synonym.isLinkable"
          :to="searchRoute(synonym.synonym)"
          class="article-content-synonyms__synonym article-content-synonyms__synonym--link"
        >
          {{ synonym.synonym }}
        </RouterLink>
        <span v-else class="article-content-synonyms__synonym">{{ synonym.synonym }}</span>
      </template>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.article-content-synonyms {
  &__header {
    color: rgb(@theme-colors[@grey][110]);
    font-weight: 500;
  }

  &__synonyms {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: 0 16px 0 56px;
    column-gap: 12px;
    color: rgb(@theme-colors[@secondary][110]);

    #media-breakpoint-up(lg, {
      margin: 0;
      padding-bottom: 50px; // equal to one line height in the article
    });
  }

  &__synonym {
    padding: 0 4px;

    &--link {
      text-decoration: underline;
    }
  }
}
</style>
